import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMemberAuth } from "@/context/MemberAuthContext";

// Active membership means status === "active" AND not past its expiry_date.
// Mirrors the backend's _get_active_membership check used to gate applications.
export function useActiveMembership() {
  const { member, ready } = useMemberAuth();
  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ["my-memberships-status"],
    queryFn: async () => (await api.get("/member/memberships")).data,
    enabled: !!member,
  });
  const today = new Date().toISOString().slice(0, 10);
  const activeMembership = (data || []).find((m) => m.status === "active" && (!m.expiry_date || m.expiry_date >= today)) || null;
  return {
    member: member || null,
    hasActive: !!activeMembership,
    activeMembership,
    isLoading: !ready || (!!member && queryLoading),
  };
}

// Foundation members with an active membership get an admin-configured discount on eligible
// Learning & Training programmes (server-enforced at checkout, defaults to 25%). Paid DAWN
// applicants get a 10% floor discount on the same programmes even without membership (server
// takes whichever percent is higher - see _compute_training_discount in server.py).
export function useTrainingDiscount() {
  const { member } = useMemberAuth();
  const { hasActive } = useActiveMembership();
  const { data: settings } = useQuery({
    queryKey: ["training-discount-settings"],
    queryFn: async () => (await api.get("/training-discount-settings")).data,
  });
  const { data: dawnDiscount } = useQuery({
    queryKey: ["dawn-discount-eligibility"],
    queryFn: async () => (await api.get("/member/dawn/discount-eligibility")).data,
    enabled: !!member,
    staleTime: 60_000,
  });
  const settingsActive = settings ? settings.active !== false : true;
  const memberPercent = hasActive && settingsActive ? Number(settings?.percent ?? 25) : 0;
  const dawnPercent = dawnDiscount?.eligible ? Number(dawnDiscount.training_discount_percent ?? 10) : 0;
  const percent = Math.max(memberPercent, dawnPercent);
  const source = memberPercent >= dawnPercent && memberPercent > 0 ? "membership" : (dawnPercent > 0 ? "dawn" : null);
  return { eligible: percent > 0, percent, source };
}
